import { useEffect, useRef, useState } from "react";
import { useWidgetSession } from "./hooks/use-widget-session";
import { useConversation } from "./hooks/use-conversation";
import { listenToParent, sendToParent } from "./lib/postmessage";
import { api } from "./lib/api";
import { Header } from "./components/Header";
import { MessageList } from "./components/MessageList";
import { Composer } from "./components/Composer";
import { RatingForm } from "./components/RatingForm";
import { PreChatForm, type PreChatValues } from "./components/PreChatForm";
import { TicketForm, type TicketValues } from "./components/TicketForm";
import { playNotificationSound, prepareNotificationSound } from "./lib/notification-sound";
import { widgetStorage } from "./lib/storage";

function isReplyMessage(senderType: string) {
  return senderType === "AI" || senderType === "AGENT";
}

function TicketSubmissionNotice({ ticketNumber, onSendAnother }: { ticketNumber: string; onSendAnother: () => void }) {
  return (
    <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3 bg-ink px-6 text-center">
      <p className="text-sm font-semibold text-white">Tiket Anda sudah tercatat</p>
      <p className="rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-xs font-medium tracking-wide text-zinc-300">{ticketNumber}</p>
      <p className="max-w-[85%] text-xs leading-relaxed text-zinc-500">
        Tim kami akan menghubungi Anda melalui email atau telepon secepatnya.
      </p>
      <button
        onClick={onSendAnother}
        className="rounded-xl border border-gold bg-gold px-4 py-2 text-sm font-semibold text-ink shadow-lg"
      >
        Kirim tiket lagi
      </button>
    </div>
  );
}

export default function App() {
  const { config, visitorToken, loading, error } = useWidgetSession();
  const {
    conversation,
    messages,
    lastIncomingReply,
    connected,
    agentTyping,
    agentTypingName,
    aiTyping,
    agentConnecting,
    agentReplyRemainingSeconds,
    agentReplyTimedOut,
    canRequestAgent,
    agentHandling,
    sendMessage,
    uploadImage,
    requestAgent,
    closeConversation,
    startNewConversation,
    createNewConversation,
    submitFeedback,
    notifyTyping,
    loadConversation,
  } = useConversation(
    visitorToken,
    config?.siteId ?? null,
    config?.presenceStatus,
    config?.settings?.agentReplyTimeoutSeconds ?? 60,
  );
  const [leadConversationId, setLeadConversationId] = useState(() => widgetStorage.getLeadConversationId());
  const [ticketInfo, setTicketInfo] = useState(() => widgetStorage.getTicketInfo());

  // Live Chat availability is a manual Widget Settings choice. Agent presence
  // must not turn an AI conversation into a ticket flow when nobody is online.
  const isOffline = config?.settings?.humanChatEnabled === false;

  async function handlePreChatSubmit(values: PreChatValues) {
    if (!visitorToken) {
      throw new Error("Session visitor belum siap. Coba lagi sebentar.");
    }

    // The pre-chat form always starts a fresh transcript. Never reuse a conversation left in
    // memory by an interrupted bootstrap or an earlier customer on the same browser.
    const currentConversation = await createNewConversation();
    const { message, ...lead } = values;
    const response = await api.post<{ id: string; syncStatus: string; conversationId?: string | null; resumedConversation?: boolean }>(
      `/api/v1/widget/conversations/${currentConversation.id}/lead`,
      lead,
      visitorToken,
    );
    const targetConversationId = response.conversationId ?? currentConversation.id;
    widgetStorage.setLeadConversationId(targetConversationId);
    setLeadConversationId(targetConversationId);

    // Send the visitor's first message (typed in the pre-chat form) before reloading, so it's in
    // the transcript and the AI answers it right after the greeting.
    const firstMessage = message.trim();
    if (firstMessage) {
      await api
        .post(
          `/api/v1/widget/conversations/${targetConversationId}/messages`,
          { content: firstMessage, clientMessageId: crypto.randomUUID() },
          visitorToken,
        )
        .catch(() => undefined);
    }

    // Reload even when the ID is unchanged so the server-created greeting is immediately visible.
    await loadConversation(targetConversationId);
  }

  async function handleTicketSubmit(values: TicketValues): Promise<string> {
    if (!visitorToken) {
      throw new Error("Session visitor belum siap. Coba lagi sebentar.");
    }

    const currentConversation = conversation ?? (await createNewConversation());
    const response = await api.post<{ id: string; ticketNumber: string }>(
      `/api/v1/widget/conversations/${currentConversation.id}/ticket`,
      values,
      visitorToken,
    );
    const info = { conversationId: currentConversation.id, ticketNumber: response.ticketNumber };
    widgetStorage.setTicketInfo(info);
    setTicketInfo(info);
    return response.ticketNumber;
  }

  function handleSendAnotherTicket() {
    widgetStorage.clearTicketInfo();
    setTicketInfo(null);
  }

  const [panelOpen, setPanelOpen] = useState(true);
  const readCountRef = useRef(0);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    sendToParent({ type: "solidchat:ready" });
    return listenToParent((msg) => {
      if (msg.type === "solidchat:identify" && visitorToken) {
        api.post("/api/v1/widget/identify", { identityToken: msg.identityToken }, visitorToken).catch(() => undefined);
      }
      if (msg.type === "solidchat:open") setPanelOpen(true);
      if (msg.type === "solidchat:close") setPanelOpen(false);
    });
  }, [visitorToken]);

  useEffect(() => prepareNotificationSound(), []);

  useEffect(() => {
    if (panelOpen) {
      readCountRef.current = messages.length;
      setUnreadCount(0);
      return;
    }
    const unseen = messages.filter((m, i) => i >= readCountRef.current && isReplyMessage(m.senderType)).length;
    setUnreadCount(unseen);
  }, [messages, panelOpen]);

  useEffect(() => {
    sendToParent({ type: "solidchat:unread", count: unreadCount });
  }, [unreadCount]);

  useEffect(() => {
    if (!lastIncomingReply) return;
    playNotificationSound();
  }, [lastIncomingReply]);

  const [showRating, setShowRating] = useState(false);
  useEffect(() => {
    const conversationEnded = conversation?.status === "RESOLVED" || conversation?.status === "CLOSED";
    setShowRating(Boolean(conversationEnded && config?.settings?.ratingFormEnabled));
  }, [conversation?.status, config?.settings?.ratingFormEnabled]);

  const conversationEnded = conversation?.status === "RESOLVED" || conversation?.status === "CLOSED";
  const leadSubmittedForConversation = !!conversation && leadConversationId === conversation.id;

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-ink text-sm text-zinc-500">Memuat SolidChat...</div>
    );
  }

  if (error || !config) {
    return (
      <div className="flex h-screen items-center justify-center bg-ink px-6 text-center text-sm text-zinc-500">
        {error ?? "Widget tidak tersedia saat ini."}
      </div>
    );
  }

  if (!config.settings?.widgetEnabled) {
    return <div className="flex h-screen items-center justify-center bg-ink px-6 text-center text-sm text-zinc-500">{config.offlineMessage}</div>;
  }

  const ticketSubmittedForConversation = !!conversation && ticketInfo?.conversationId === conversation.id;

  if (isOffline && !leadSubmittedForConversation) {
    return (
      <div className="flex h-screen min-h-0 flex-col overflow-hidden rounded-2xl border border-zinc-800 shadow-2xl">
        <Header
          config={config}
          liveChatOnline={config.settings?.humanChatEnabled ?? false}
          canStartNew={false}
          canEndConversation={false}
          onStartNewConversation={() => undefined}
          onEndConversation={() => undefined}
        />
        {ticketSubmittedForConversation && ticketInfo ? (
          <TicketSubmissionNotice ticketNumber={ticketInfo.ticketNumber} onSendAnother={handleSendAnotherTicket} />
        ) : (
          <TicketForm
            widgetColor={config.widgetColor}
            offlineMessage={config.offlineMessage}
            onSubmit={handleTicketSubmit}
            onSendAnother={handleSendAnotherTicket}
          />
        )}
      </div>
    );
  }

  if (!leadSubmittedForConversation) {
    return (
      <div className="flex h-screen min-h-0 flex-col overflow-hidden rounded-2xl border border-zinc-800 shadow-2xl">
        <Header
          config={config}
          liveChatOnline={config.settings?.humanChatEnabled ?? false}
          canStartNew={false}
          canEndConversation={false}
          onStartNewConversation={() => undefined}
          onEndConversation={() => undefined}
        />
        <div className="min-h-0 flex-1 overflow-y-auto">
          <PreChatForm
            widgetColor={config.widgetColor}
            onSubmit={handlePreChatSubmit}
          />
        </div>
      </div>
    );
  }

  if (isOffline && !conversationEnded) {
    return (
      <div className="flex h-screen min-h-0 flex-col overflow-hidden rounded-2xl border border-zinc-800 shadow-2xl">
        <Header
          config={config}
          liveChatOnline={config.settings?.humanChatEnabled ?? false}
          canStartNew={false}
          canEndConversation={false}
          onStartNewConversation={() => undefined}
          onEndConversation={() => undefined}
        />
        {ticketSubmittedForConversation && ticketInfo ? (
          <TicketSubmissionNotice ticketNumber={ticketInfo.ticketNumber} onSendAnother={handleSendAnotherTicket} />
        ) : (
          <div className="min-h-0 flex-1 overflow-y-auto">
            <TicketForm
              widgetColor={config.widgetColor}
              offlineMessage={config.offlineMessage}
              onSubmit={handleTicketSubmit}
              onSendAnother={handleSendAnotherTicket}
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="relative flex h-screen min-h-0 flex-col overflow-hidden rounded-2xl border border-zinc-800 shadow-2xl">
      <Header
        config={config}
        liveChatOnline={config.settings?.humanChatEnabled ?? false}
        canStartNew={conversationEnded}
        canEndConversation={!conversationEnded && !!conversation}
        onStartNewConversation={() => {
          setShowRating(false);
          widgetStorage.clearLeadConversationId();
          setLeadConversationId(null);
          startNewConversation().catch(() => undefined);
        }}
        onEndConversation={closeConversation}
      />
      <div className="relative flex min-h-0 flex-1 flex-col">
        <MessageList
          messages={messages}
          config={config}
          agentTyping={agentTyping}
          agentTypingName={agentTypingName}
          aiTyping={aiTyping}
          agentConnecting={agentConnecting}
          agentReplyRemainingSeconds={agentReplyRemainingSeconds}
          agentReplyTimedOut={agentReplyTimedOut}
          visitorToken={visitorToken ?? ""}
        />
        {showRating ? (
          <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center bg-black/75 px-4">
            <div className="pointer-events-auto w-full">
              <RatingForm
                widgetColor={config.widgetColor}
                onSubmit={async (score, comment) => {
                  await submitFeedback(score, comment);
                  setShowRating(false);
                }}
              />
            </div>
          </div>
        ) : null}
      </div>
      {conversationEnded ? (
        <div className="border-t border-zinc-800 bg-ink p-4">
          <div className="rounded-2xl border border-zinc-800 bg-zinc-950 px-4 py-4 text-center">
            <p className="text-sm font-medium text-zinc-100">Percakapan ini sudah diakhiri.</p>
            <p className="mt-1 text-xs leading-relaxed text-zinc-400">Pilih Pesan Baru untuk memulai percakapan baru.</p>
            <button
              onClick={() => {
                setShowRating(false);
                widgetStorage.clearLeadConversationId();
                setLeadConversationId(null);
                startNewConversation().catch(() => undefined);
              }}
              className="mt-3 rounded-xl px-4 py-2 text-sm font-medium text-ink"
              style={{ backgroundColor: config.widgetColor }}
            >
              Pesan Baru
            </button>
          </div>
        </div>
      ) : (
        <Composer
          config={config}
          disabled={!connected}
          onSend={sendMessage}
          onUploadImage={uploadImage}
          onTyping={notifyTyping}
          onRequestAgent={requestAgent}
          canRequestAgent={canRequestAgent}
          canUploadImage={agentHandling}
        />
      )}
    </div>
  );
}
